import {useState} from 'react'
import DatePicker from 'react-datepicker'
import exportIcon from 'shared/assets/document.svg'
import refreshIcon from 'shared/assets/refresh.svg'
import uploadIcon from 'shared/assets/upload.svg'
import useGetTicketStatistics from 'entity/admin/useGetTicketStatistics'
import useGetTicketStatisticsExport from 'entity/admin/useGetTicketStatisticsExport'
import useAutoRefresh from 'shared/hooks/useAutoRefresh'
import Style from './style'

const TicketUsageOverview = () => {
	const [selectedDate, setSelectedDate] = useState(new Date())
	const [ticketStatistics, isLoading, refreshRequest] =
		useGetTicketStatistics(selectedDate)
	const [, exportTicketStatistics, isExporting] = useGetTicketStatisticsExport()

	// Use auto-refresh hook with 15-minute interval
	const {nextRefreshTime, handleManualRefresh} = useAutoRefresh(
		refreshRequest,
		15
	)
	// Get current data from API response
	const summary = ticketStatistics?.data?.summary || {
		total_seed_exchanged: 0,
		total_direct_issued: 0,
		grand_total: 0,
	}

	const seriesData = ticketStatistics?.data?.series_breakdown || []

	// Transform API data to match component structure

	const tournamentData = seriesData.map((series, index) => {
		const total_exchanged_count = series.tickets.reduce(
			(sum, ticket) => sum + ticket.exchanged_count,
			0
		)
		const total_direct_issued_count = series.tickets.reduce(
			(sum, ticket) => sum + ticket.direct_issued_count,
			0
		)
		return {
			id: index + 1,
			title: series.series_name,
			tickets: total_exchanged_count + total_direct_issued_count,
			seeds: series.series_total,
			events: series.tickets.map((ticket) => ({
				name: ticket.ticket_name || 'Unknown Ticket',
				used: ticket.total_count,
				total: ticket.total_count || 0,
				exchanged: ticket.exchanged_count || 0,
				direct: ticket.direct_issued_count || 0,
			})),
		}
	})

	const handleRefresh = () => {
		handleManualRefresh()
	}

	const handleExport = () => {
		exportTicketStatistics()
	}

	return (
		<Style.Container>
			{/* Header Section */}

			<Style.Header>
				<Style.TitleSection>
					<Style.Title>Ticket Usage Overview</Style.Title>
					<Style.Subtitle>
						Last updated:{' '}
						{isLoading
							? 'Loading...'
							: new Date().toLocaleString('en-US', {
									year: 'numeric',
									month: 'long',
									day: 'numeric',
									hour: 'numeric',
									minute: '2-digit',
									timeZoneName: 'short',
							  })}
					</Style.Subtitle>
				</Style.TitleSection>
				<Style.Dropdown>
					<DatePicker
						selected={selectedDate}
						onChange={(date) => setSelectedDate(date)}
						showYearPicker
						dateFormat='yyyy'
					/>
				</Style.Dropdown>
				<Style.ButtonGroup>
					<Style.RefreshText>
						Data refresh interval: in{' '}
						{Math.ceil((nextRefreshTime.getTime() - Date.now()) / (1000 * 60))}{' '}
						minutes
					</Style.RefreshText>
					<Style.Button onClick={handleRefresh}>
						<img src={refreshIcon} alt='refresh' />
						<Style.ButtonText>Refresh Data</Style.ButtonText>
					</Style.Button>
					<Style.Button onClick={handleExport} disabled={isExporting}>
						<img src={exportIcon} alt='export' />
						<Style.ButtonText>
							{isExporting ? 'Exporting...' : 'Export XLSX'}
						</Style.ButtonText>
					</Style.Button>
				</Style.ButtonGroup>
			</Style.Header>

			{/* Usage Info */}
			<Style.UsageInfo>
				<Style.UsageText color='#ED3241'>ticket used</Style.UsageText>
				<Style.UsageText color='#1F2024'>/total issued</Style.UsageText>
			</Style.UsageInfo>

			{/* Cards Section */}
			<Style.CardsSection>
				{/* Total Issued Card */}
				<Style.TotalCard>
					<Style.TotalCardContent>
						<Style.TotalCardTitle>Total issued</Style.TotalCardTitle>
						<Style.UploadIcon>
							<img src={uploadIcon} alt='upload' />
						</Style.UploadIcon>
						<Style.TotalCardData>
							<Style.TotalCardValue>
								{isLoading
									? 'Loading...'
									: `${(summary.total_direct_issued + summary.total_seed_exchanged).toLocaleString()} tickets`}
							</Style.TotalCardValue>
							<Style.TotalCardValue>
								{isLoading
									? 'Loading...'
									: `${summary.grand_total.toLocaleString()} seeds`}
							</Style.TotalCardValue>
						</Style.TotalCardData>
					</Style.TotalCardContent>
				</Style.TotalCard>

				{/* Tournament Cards */}
				<Style.TournamentCards>
					{isLoading ? (
						<Style.TournamentCard>
							<Style.TournamentHeader>
								<Style.TournamentTitle>Loading...</Style.TournamentTitle>
							</Style.TournamentHeader>
						</Style.TournamentCard>
					) : (
						tournamentData.map((tournament) => (
							<Style.TournamentCard key={tournament.id}>
								<Style.TournamentHeader>
									<Style.TournamentTitle>
										{tournament.title}
									</Style.TournamentTitle>
									<Style.TournamentStats>
										<Style.StatText>
											{tournament.tickets.toLocaleString()} tickets
										</Style.StatText>
										<Style.StatText>/</Style.StatText>
										<Style.StatText>
											{tournament.seeds.toLocaleString()} seeds
										</Style.StatText>
									</Style.TournamentStats>
								</Style.TournamentHeader>
								<Style.TournamentContent>
									{tournament.events.map((event, index) => {
										const usedTicket = event.exchanged + event.direct
										return (
											<Style.EventCard key={index}>
												<Style.EventInfo>
													<Style.EventName>{event.name}</Style.EventName>
													<Style.EventSeparator>|</Style.EventSeparator>
													<Style.EventStatsUsed>
														{usedTicket.toLocaleString()}
													</Style.EventStatsUsed>
													<Style.EventStats>
														/{event.total.toLocaleString()}
													</Style.EventStats>
												</Style.EventInfo>
											</Style.EventCard>
										)
									})}
								</Style.TournamentContent>
							</Style.TournamentCard>
						))
					)}
				</Style.TournamentCards>
			</Style.CardsSection>
		</Style.Container>
	)
}

export default TicketUsageOverview
